import { IconButton } from '@material-ui/core';
import get from 'lodash/get';
import React, { useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { ReactComponent as HelpIcon } from '../../../../assets/images/help-md.svg';
import Config from '../../../../config';
import useDialog from '../../../../ui/hooks/useDialog';
import { withThemeNext } from '../../../../ui/layout/theme-next';
import HelpAgentPanel from './components/HelpAgentPanel/HelpAgentPanel';
import HelpCenterContent from './components/HelpCenterContent/HelpCenterContent';
import HelpCenterNavigation from './components/HelpCenterNavigation/HelpCenterNavigation';
import en from './data/content-en';
import useHelpModule from './hooks/useHelpModule';
import usePreviousValue from './hooks/usePreviousValue';

const helpContent = {
  en,
};

interface Props {
  persistent?: boolean
  draggable?: boolean
  className?: string
}

export const HelpCenter: React.FC<Props> = ({ persistent, className }) => {
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [isAiHelpOpen, setIsAiHelpOpen] = React.useState(false);
  const helpButtonRef = useRef<HTMLButtonElement | null>(null);
  const openHelp = (event: React.MouseEvent<HTMLButtonElement>) => {
    if (Config.AiHelpWidgetEnabled) {
      setIsAiHelpOpen(true);
      return;
    }
    setAnchorEl(event.currentTarget);
  };
  const closeMenu = () => {
    setAnchorEl(null);
  };

  const contentDialog = useDialog();
  const openContent = () => {
    closeMenu();
    contentDialog.open();
  };
  const currentModule = useHelpModule();
  const { locale, defaultLocale, formatMessage } = useIntl();
  const localizedContent = get(helpContent, locale, helpContent[defaultLocale]);
  const currentContent = get(localizedContent, currentModule, null);

  const previousModule = usePreviousValue(currentModule);
  useEffect(() => {
    if (!persistent && previousModule && currentModule !== previousModule) {
      contentDialog.close();
      setIsAiHelpOpen(false);
    }
  }, [persistent, contentDialog, currentModule, previousModule]);

  return (
    <>
      <IconButton
        ref={helpButtonRef}
        className={className}
        color="inherit"
        size="small"
        onClick={openHelp}
        aria-label={formatMessage({ id: 'help.title' })}
        aria-expanded={Boolean(anchorEl) || isAiHelpOpen}
        data-cy="help-center.opener"
      >
        <HelpIcon />
      </IconButton>
      {!Config.AiHelpWidgetEnabled && (
        <HelpCenterNavigation
          anchorEl={anchorEl}
          hasGuide={Boolean(currentContent)}
          handleCloseMenu={closeMenu}
          handleOpenContent={openContent}
        />
      )}
      {currentContent && (
        <HelpCenterContent
          content={currentContent}
          opened={contentDialog.isOpen}
          handleCloseDialog={contentDialog.close}
        />
      )}
      {Config.AiHelpWidgetEnabled && (
        <HelpAgentPanel
          open={isAiHelpOpen}
          anchorEl={helpButtonRef.current}
          onClose={() => setIsAiHelpOpen(false)}
        />
      )}
    </>
  );
};

export default withThemeNext(HelpCenter);
